// src/components/SubscriptionPayment.js

import React, { useState, useContext, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from '@mui/material';
import { AuthContext } from '../context/AuthContext';
import SolanaPaymentModal from './SolanaPaymentModal';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

const SubscriptionPayment = ({ subscription, onClose }) => {
  const { guilds, setGuildId, refreshUser } = useContext(AuthContext);
  const [selectedGuildId, setSelectedGuildId] = useState('');
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    console.log('SubscriptionPayment: Received guilds:', guilds);
    console.log('SubscriptionPayment: Received subscription:', subscription);
  }, [guilds, subscription]);

  const handleGuildChange = (event) => {
    setSelectedGuildId(event.target.value);
    console.log('Selected Guild ID:', event.target.value);
  };

  const handleProceedToPayment = () => {
    if (!selectedGuildId) {
      alert('Please select a guild to attach the subscription.');
      console.log('No guild selected. Payment cannot proceed.');
      return;
    }
    setGuildId(selectedGuildId); // Update the guild ID in AuthContext
    setPaymentModalOpen(true);
    console.log('Proceeding to payment with Guild ID:', selectedGuildId);
  };

  const handlePaymentClose = () => {
    setPaymentModalOpen(false);
    onClose(); // Close the subscription payment flow
  };

  const handlePaymentSuccess = async (transactionDetails) => {
    console.log('Payment successful:', transactionDetails);
    alert('Payment successful! Your subscription is now active.');
    setPaymentModalOpen(false);
    onClose(); // Close the subscription payment flow
    await refreshUser(); // Refresh user data to include subscription
    console.log('Navigating to /setup');
    navigate('/setup');
  };

  const handlePaymentFailure = (error) => {
    console.error('Payment failed:', error);
    alert('Payment failed. Please try again.');
  };

  const getMaxCollectionsAllowed = (level) => {
    switch (level.toLowerCase()) {
      case 'basic':
        return 1;
      case 'pro':
        return 5;
      case 'premium':
        return 10;
      default:
        return 1;
    }
  };

  return (
    <>
      {/* Guild Selection Dialog */}
      <Dialog open={!paymentModalOpen} onClose={handlePaymentClose} disableEscapeKeyDown={false}>
        <DialogTitle>Select Guild for Subscription</DialogTitle>
        <DialogContent>
          {guilds.length === 0 ? (
            <Typography>
              You don't have administrative permissions for any guilds. Please ensure you have the necessary permissions or contact the guild owner.
            </Typography>
          ) : (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="guild-select-label">Select Guild</InputLabel>
              <Select
                labelId="guild-select-label"
                id="guild-select"
                value={selectedGuildId}
                label="Select Guild"
                onChange={handleGuildChange}
              >
                {guilds.map((guild) => (
                  <MenuItem key={guild.id} value={guild.id}>
                    {guild.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePaymentClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleProceedToPayment}
            disabled={!selectedGuildId}
          >
            Select Wallet & Proceed to Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Solana Payment Modal */}
      {paymentModalOpen && (
        <SolanaPaymentModal
          open={paymentModalOpen}
          handleClose={handlePaymentClose}
          amount={subscription.amount}
          recipient={process.env.REACT_APP_PAYMENT_WALLET_ADDRESS}
          paymentReason="subscription"
          selectedGuild={selectedGuildId}
          onPaymentSuccess={handlePaymentSuccess}
          onPaymentFailure={handlePaymentFailure}
          subscriptionLevel={subscription.title.toLowerCase()}
          maxCollectionsAllowed={getMaxCollectionsAllowed(subscription.title)}
        />
      )}
    </>
  );
};

SubscriptionPayment.propTypes = {
  subscription: PropTypes.shape({
    amount: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    // ... other fields if necessary
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default SubscriptionPayment;


